import { SyntaxNode } from '@lezer/common';

import { tryParseJSON } from '@/utils/try-parse-json';

import { parser } from './lang';

// comparison
// - comparator
// - identifier?
// - literal

// operation
// - operand
// - argument: operation | comparison

// literal
// - boolean
// - null
// - number
// - string
// - array

// program: comparison | operation

const getValue = (node: SyntaxNode | null, program: string) => {
  if (!node) return;
  return program.slice(node.from, node.to);
};

export interface Comparison {
  comparator: string;
  identifier: string | undefined;
  argument: string | number | boolean | null | Array<string | number | boolean>;
  metadata?: Record<string, unknown>;

  // depth is used to keep track of the depth of the comparison
  depth?: number;
}

const comparison = (
  node: SyntaxNode | null,
  program: string
): Comparison | undefined => {
  if (!node || node.name !== 'Comparison') return undefined;

  const comparator =
    getValue(node.getChild('Comparator'), program) ||
    getValue(node.getChild('GlobalComparator'), program) ||
    getValue(node.getChild('like'), program) ||
    getValue(node.getChild('notlike'), program);
  if (!comparator) return undefined;

  let comparisonValue = getValue(node.getChild('ComparisonValue'), program);
  if (!comparisonValue) return undefined;

  let identifier = getValue(node.getChild('Identifier'), program);
  if (
    (identifier?.startsWith("'") && identifier.endsWith("'")) ||
    (identifier?.startsWith('"') && identifier.endsWith('"'))
  ) {
    identifier = identifier.slice(1, -1);
  }

  // JSON.parse does not support single quotes
  // as we do not handle escaping single quotes, we can afford to trim edges
  // and convert to valid JSON string with escaping double quotes
  if (comparisonValue.startsWith("'") && comparisonValue.endsWith("'")) {
    comparisonValue = JSON.stringify(comparisonValue.slice(1, -1));
  }

  let argument = tryParseJSON(comparisonValue);
  if (argument == null) {
    return undefined;
  }

  // like and notlike comparators contains % before and after the argument
  if (
    (comparator === 'like' || comparator === 'notlike') &&
    argument.startsWith('%') &&
    argument.endsWith('%')
  ) {
    argument = argument.slice(1, -1);
  }

  if (
    typeof argument !== 'string' &&
    typeof argument !== 'number' &&
    typeof argument !== 'boolean' &&
    argument !== null &&
    Array.isArray(argument) &&
    argument.every(
      (x) =>
        typeof x !== 'string' && typeof x !== 'number' && typeof x !== 'boolean'
    )
  ) {
    return undefined;
  }

  return { comparator, identifier, argument };
};

interface Operation {
  operator: string;
  operands: Array<Operation | Comparison>;
}

const operation = (
  node: SyntaxNode | null,
  program: string
): Operation | undefined => {
  if (!node || node.name !== 'Operation') return undefined;

  const operatorNode = node.getChild('Operator');
  const operator = getValue(operatorNode, program);
  if (!operator) return undefined;

  const args: Array<Operation | Comparison> = [];

  const cursor = node.firstChild?.cursor();
  if (!cursor) return undefined;

  do {
    const child = cursor.node;

    if (child.name === 'Operation') {
      const value = operation(child, program);
      if (value) args.push(value);
    }

    if (child.name === 'Comparison') {
      const value = comparison(child, program);
      if (value) args.push(value);
    }
  } while (cursor.nextSibling());

  return {
    operator,
    operands: args,
  };
};

const program = (node: SyntaxNode | null, program: string) => {
  if (!node) return undefined;

  if (node.firstChild?.name === 'Operation') {
    return operation(node.firstChild, program);
  }

  if (node.firstChild?.name === 'Comparison') {
    return comparison(node.firstChild, program);
  }
};
type RunsFilterAst = Comparison | Operation | undefined;

export const stringToAst = (
  input: string | undefined | null
): RunsFilterAst => {
  if (input == null) return undefined;
  const tree = parser.parse(input);
  return program(tree.topNode, input);
};

type PartialComparison = Partial<Comparison>;
type PartialOperation = {
  operator: string;
  operands: Array<PartialComparison | PartialOperation>;
};

export type PartialRunsFilterAst =
  | PartialComparison
  | PartialOperation
  | undefined;

export const flattenAst = (
  ast: PartialRunsFilterAst
): { operator: 'and'; operands: Array<PartialComparison> } | undefined => {
  if (!ast) return undefined;

  if ('comparator' in ast) {
    return { operator: 'and', operands: [{ ...ast, depth: 0 }] };
  }

  if ('operator' in ast && ast.operator === 'and') {
    const operands: Array<PartialComparison> = [];
    const queue: Array<{
      item: PartialOperation | PartialComparison;
      depth: number;
    }> = [...ast.operands.map((item) => ({ item, depth: 0 }))];

    while (queue.length > 0) {
      const current = queue.shift();
      if (!current) continue;

      if ('operator' in current.item) {
        if (current.item.operator === 'and') {
          queue.unshift(
            ...current.item.operands.map((item) => ({
              item,
              depth: current.depth + 1,
            }))
          );
        } else {
          return undefined;
        }
      } else if ('comparator' in current.item) {
        operands.push({ ...current.item, depth: current.depth });
      } else {
        operands.push({});
      }
    }
    return { operator: ast.operator, operands };
  }

  return undefined;
};

export const astToString = (
  ast: PartialRunsFilterAst,
  _level = 0
): string | undefined => {
  if (!ast) return undefined;
  if ('operator' in ast) {
    const operands = ast.operands
      .map((op) => astToString(op, _level + 1))
      .filter((x) => x != null);

    if (operands.length === 0) {
      return undefined;
    }

    if (operands.length === 1) return operands[0];
    return `${ast.operator}(${operands.join(', ')})`;
  }

  if (!ast.comparator || typeof ast.argument === 'undefined') return undefined;
  const isMatch = ast.comparator === 'like' || ast.comparator === 'notlike';
  const argument = isMatch
    ? `"%${ast.argument}%"`
    : JSON.stringify(ast.argument);

  if (ast.identifier) {
    return `${ast.comparator}(${ast.identifier}, ${argument})`;
  }

  return `${ast.comparator}(${argument})`;
};

export function isSameComparison(
  a: Partial<Comparison>,
  b: Partial<Comparison>
) {
  return (
    a.comparator === b.comparator &&
    a.identifier === b.identifier &&
    a.argument === b.argument
  );
}

export function isSameComparisons(
  a: Partial<Comparison>[],
  b: Partial<Comparison>[]
) {
  return (
    a.length === b.length &&
    a.every((comp) => b.find((bComp) => isSameComparison(comp, bComp)))
  );
}

export const findComparsion = (
  comparator: PartialComparison['comparator'],
  identifier: PartialComparison['identifier'],
  argument: PartialComparison['argument']
) => {
  return (comparison: PartialComparison) => {
    if (!comparison) return false;
    return isSameComparison(comparison, { comparator, identifier, argument });
  };
};
